import React, { useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  BellIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
  XMarkIcon,
  CheckIcon,
} from '@heroicons/react/24/outline'
import { notificationsAPI } from '../../services/api'

const TYPE_ICON = {
  ocr_completed: { Icon: CheckCircleIcon, color: '#167e75' },
  ocr_failed: { Icon: XCircleIcon, color: '#e55353' },
  document_uploaded: { Icon: DocumentTextIcon, color: '#3B82F6' },
  budget_exceeded: { Icon: XCircleIcon, color: '#f59e0b' },
}

function timeAgo(dateStr, locale) {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return locale.startsWith('pl') ? 'przed chwilą' : 'just now'
  if (mins < 60) return locale.startsWith('pl') ? `${mins} min temu` : `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return locale.startsWith('pl') ? `${hours} godz. temu` : `${hours}h ago`
  const days = Math.floor(hours / 24)
  return locale.startsWith('pl') ? `${days} dni temu` : `${days}d ago`
}

const NotificationsPanel = ({ onClose, locale }) => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const panelRef = useRef(null)

  const { data, isLoading } = useQuery(
    'notifications',
    () => notificationsAPI.getNotifications({ limit: 20 }),
    { refetchInterval: 30000, staleTime: 10000 }
  )

  const markAsRead = useMutation(
    (id) => notificationsAPI.markAsRead(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('notifications')
        queryClient.invalidateQueries('notificationsUnread')
      },
    }
  )

  const markAll = useMutation(
    () => notificationsAPI.markAllAsRead(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('notifications')
        queryClient.invalidateQueries('notificationsUnread')
      },
    }
  )

  const deleteOne = useMutation(
    (id) => notificationsAPI.deleteNotification(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('notifications')
        queryClient.invalidateQueries('notificationsUnread')
      },
    }
  )

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [onClose])

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead.mutate(notification.id)
    }
    if (notification.related_document_id) {
      navigate(`/documents/${notification.related_document_id}`)
      onClose()
    }
  }

  const notifications = data?.data?.notifications || []
  const unreadCount = data?.data?.unread_count || 0

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-[calc(100%+8px)] z-50 w-80 rounded-2xl overflow-hidden"
      style={{
        background: 'var(--color-overlay)',
        border: '1px solid var(--color-border)',
        boxShadow: '0 20px 50px rgba(20,30,40,.15)',
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3"
        style={{ borderBottom: '1px solid var(--color-border-strong)' }}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold" style={{ color: 'var(--color-text)' }}>
            {t('notifications')}
          </span>
          {unreadCount > 0 && (
            <span
              className="px-1.5 py-0.5 rounded-full text-xs font-bold"
              style={{ background: '#e55353', color: '#fff' }}
            >
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {unreadCount > 0 && (
            <button
              type="button"
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold transition-colors"
              style={{ color: '#1f9f93' }}
              onClick={() => markAll.mutate()}
              title={t('markAllRead')}
            >
              <CheckIcon className="h-3.5 w-3.5" />
              {t('markAllRead')}
            </button>
          )}
          <button
            type="button"
            className="w-7 h-7 rounded-lg grid place-items-center transition-colors"
            style={{ color: 'var(--color-text-faint)' }}
            onClick={onClose}
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* List */}
      <div className="overflow-y-auto" style={{ maxHeight: '400px' }}>
        {isLoading ? (
          <div className="px-4 py-8 text-center text-sm" style={{ color: 'var(--color-text-faint)' }}>
            {t('loading')}
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-4 py-10">
            <BellIcon className="h-10 w-10" style={{ color: 'var(--color-icon-faint)' }} />
            <p className="text-sm font-medium" style={{ color: 'var(--color-text-faint)' }}>
              {t('noNotifications')}
            </p>
          </div>
        ) : (
          notifications.map((n) => {
            const cfg = TYPE_ICON[n.type] || TYPE_ICON.document_uploaded
            const Icon = cfg.Icon
            return (
              <div
                key={n.id}
                className="group relative flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors"
                style={{
                  background: n.is_read ? 'transparent' : 'rgba(31,159,147,.04)',
                  borderBottom: '1px solid var(--color-border-strong)',
                }}
                onClick={() => handleNotificationClick(n)}
              >
                {/* Icon */}
                <div
                  className="mt-0.5 w-8 h-8 rounded-xl flex-shrink-0 grid place-items-center"
                  style={{ background: cfg.color + '18' }}
                >
                  <Icon className="h-4 w-4" style={{ color: cfg.color }} />
                </div>

                {/* Text */}
                <div className="flex-1 min-w-0 pr-6">
                  <p
                    className="text-xs font-bold leading-tight"
                    style={{ color: 'var(--color-text)', opacity: n.is_read ? 0.6 : 1 }}
                  >
                    {n.title}
                  </p>
                  {n.message && (
                    <p
                      className="text-xs mt-0.5 leading-snug"
                      style={{ color: 'var(--color-text-faint)' }}
                    >
                      {n.message}
                    </p>
                  )}
                  <p className="text-[10px] mt-1" style={{ color: 'var(--color-icon-muted)' }}>
                    {timeAgo(n.createdAt || n.created_at, locale)}
                  </p>
                </div>

                {/* Delete button */}
                <button
                  type="button"
                  className="absolute right-2 top-2 w-6 h-6 rounded-lg grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ color: 'var(--color-icon-muted)' }}
                  onClick={(e) => {
                    e.stopPropagation()
                    deleteOne.mutate(n.id)
                  }}
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

export default NotificationsPanel
